import { TableProps } from "@amzn/awsui-components-react";
import { useAppSelector } from "app/store";
import { Link } from "react-router-dom";
import { createLabelFunction, dateFieldComparator } from "utils/table-utils";
import { useAppDispatch } from "app/store";
import { getProgressUpdated } from "pages/audits/utils";
import { setGaiaObjectId } from "../../features/gaiaObjects/slice";
import { formatDateValue, pluralize } from "utils/date-format";
import { attributeSchemaToColDef } from "utils/dynamicAttributesUtils";
import { TemplateOption } from "components/templates-table/definitions";
import TableItemStatus from "components/table-item-status";
import { formatMonetaryValue } from "pages/audit-overview/definitions";
import { getCurrencySignFromCountry } from "features/ui-content/utils";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import { getStatusDetail } from "pages/audits/table-config";
import { TEAM_NAME_MAP } from "utils/gaia-constants";

// Common fields that are available for guest users across all teams. Note, some common fields are from custom attributes.
export const COMMON_GAIA_OBJECT_FIELDS = [
  "shortId",
  "title",
  "status",
  "statusDetail",
  "progressUpdated",
  "statusActionOwner",
  "owner",
  "effort",
];

export const GUEST_GAIA_OBJECT_FIELDS = [
  ...COMMON_GAIA_OBJECT_FIELDS,
  "teamName",
];

export const GoToGaiaObjectOverviewPage = (item: any) => {
  const dispatch = useAppDispatch();
  return (
    <div>
      <Link
        to={`/${pluralize(item.category)}/${item.shortId}`}
        className="audit-overv"
        style={{ color: "#0972D3", textDecoration: "none" }}
        onClick={() => {
          dispatch(setGaiaObjectId(item.gaiaObjectId));
        }}
      >
        {item.title}
      </Link>
    </div>
  );
};

export const useRawColumnDefinitionsByTeam = (category: string) => {
  const teamName = useAppSelector((state) => state.audits.headerSelectedTeam);
  const customAttributes = useAppSelector(
    (state) =>
      state.teamSettings.selectedTeamMainMetaData.teamCustomAttributesSchema?.[
        category
      ]
  );

  let rawColumnDefinitions: TableProps<any>["columnDefinitions"] = [
    {
      id: "shortId",
      header: "ID",
      cell: (item) => item.shortId,
      ariaLabel: createLabelFunction("Id"),
      sortingField: "shortId",
      width: 120,
    },
    {
      id: "title",
      header: "Name",
      cell: (item) => GoToGaiaObjectOverviewPage(item),
      ariaLabel: createLabelFunction("Title"),
      sortingField: "title",
      width: 420,
    },
    {
      id: "status",
      header: "Status",
      cell: (item) => (
        <TableItemStatus
          stringStatus={item?.customAttributesDataMap?.["status"]}
        />
      ),
      ariaLabel: createLabelFunction("Status"),
      sortingField: "status",
      width: 160,
    },
    {
      id: "statusDetail",
      header: "Progress detail",
      cell: (item) =>
        getStatusDetail(item?.customAttributesDataMap?.["statusDetail"] || ""),
      ariaLabel: createLabelFunction("Progress detail"),
      sortingField: "statusDetail",
      width: 420,
    },
    {
      id: "progressUpdated",
      header: "Progress updated",
      cell: (item) => (item != undefined ? getProgressUpdated(item) : ""),
      ariaLabel: createLabelFunction("Progress updated"),
      sortingField: "progressUpdated",
      width: 160,
    },
    {
      id: "createdBy",
      header: "Created By",
      cell: (item) => item.createdBy,
      ariaLabel: createLabelFunction("Created By"),
      sortingField: "createdBy",
      width: 160,
    },
    {
      id: "createdAt",
      header: "Created At",
      cell: (item) => formatDateValue(item.createdAt),
      ariaLabel: createLabelFunction("Created At"),
      sortingField: "createdAt",
      sortingComparator: dateFieldComparator("createdAt"),
      width: 160,
    },
    {
      id: "modifiedBy",
      header: "Modified By",
      cell: (item) => item.modifiedBy,
      ariaLabel: createLabelFunction("Modified By"),
      sortingField: "modifiedBy",
      width: 160,
    },
    {
      id: "modifiedAt",
      header: "Modified At",
      cell: (item) => formatDateValue(item.modifiedAt),
      ariaLabel: createLabelFunction("Modified At"),
      sortingField: "modifiedAt",
      sortingComparator: dateFieldComparator("modifiedAt"),
      width: 160,
    },
    {
      id: "assessmentTotal",
      header: "Assessment total",
      cell: (item) =>
        item.assessmentTotal && item.assessmentTotal !== "0.0"
          ? formatMonetaryValue(
              item.assessmentTotal,
              getCurrencySignFromCountry(item.country, uiContentForItem)
            )
          : "-",
      ariaLabel: createLabelFunction("Assessment Total"),
      sortingField: "assessmentTotal",
      width: 150,
    },
    {
      id: "paymentTotal",
      header: "Payment total",
      cell: (item) =>
        item.paymentTotal && item.paymentTotal !== "0.0"
          ? formatMonetaryValue(
              item.paymentTotal,
              getCurrencySignFromCountry(item.country, uiContentForItem)
            )
          : "-",
      ariaLabel: createLabelFunction("Payment Total"),
      sortingField: "paymentTotal",
      width: 150,
    },
    {
      id: "progressModifiedBy",
      header: "Progress Update By",
      cell: (item) => item.progressModifiedBy,
      ariaLabel: createLabelFunction("Progress Update By"),
      sortingField: "progressModifiedBy",
      width: 160,
    },
    {
      id: "progressModifiedAt",
      header: "Progress Update At",
      cell: (item) => formatDateValue(item.progressModifiedAt),
      ariaLabel: createLabelFunction("Progress Update At"),
      sortingField: "progressModifiedAt",
      sortingComparator: dateFieldComparator("progressModifiedAt"),
      width: 160,
    },
    {
      id: "statusActionOwner",
      header: "Action pending",
      cell: (item) => item.statusActionOwner,
      ariaLabel: createLabelFunction("Action pending"),
      sortingField: "statusActionOwner",
      width: 160,
    },
    {
      id: "effort",
      header: "Effort",
      cell: (item) => item?.customAttributesDataMap?.effort || "-",
      ariaLabel: createLabelFunction("Effort"),
      sortingField: "effort",
      sortingComparator: (a, b) =>
        Number(a.customAttributesDataMap?.effort || 0) -
        Number(b.customAttributesDataMap?.effort || 0),
      width: 120,
    },
  ];
  const uiContentForItem = useGaiaObjectUiContent(category);

  if (teamName === "guest") {
    rawColumnDefinitions = [
      ...rawColumnDefinitions,
      {
        id: "teamName",
        header: "Team",
        cell: (item) => TEAM_NAME_MAP[item.teamName],
        ariaLabel: createLabelFunction("Team"),
        sortingField: "teamName",
        width: 120,
      },
    ];
    const guestBasicColumns = rawColumnDefinitions.filter((col) =>
      GUEST_GAIA_OBJECT_FIELDS.includes(col.id as any)
    );
    return guestBasicColumns;
  }

  if (customAttributes) {
    const baseColumnIds = new Set(rawColumnDefinitions.map((col) => col.id));
    const newColumns = customAttributes
      .filter((attr) => !baseColumnIds.has(attr.attributeName))
      .map(attributeSchemaToColDef);

    const hasEntityAttribute = customAttributes.some(
      (attr) => attr.attributeName === "entity"
    );
    if (hasEntityAttribute) {
      const entityCodeColumn = {
        id: "entityCode",
        header: "Entity code",
        cell: (item: any) => {
          return item?.customAttributesDataMap?.entityCode || "-";
        },
        ariaLabel: createLabelFunction("Entity code"),
        sortingField: "entityCode",
        sortingComparator: (a: any, b: any) => {
          const aCode = a?.customAttributesDataMap?.entityCode || "";
          const bCode = b?.customAttributesDataMap?.entityCode || "";
          return aCode.localeCompare(bCode);
        },
        width: 200,
      };
      newColumns.push(entityCodeColumn);
    }

    const columnDefinitions = [...rawColumnDefinitions, ...newColumns];
    return columnDefinitions;
  }

  return rawColumnDefinitions;
};

export const useTeamSpecificGaiaObjectTemplates = (category: string) => {
  switch (category) {
    case "notice": {
      return new Map<string, TemplateOption[]>([]);
    }
    case "litigation": {
      return new Map<string, TemplateOption[]>([]);
    }
    default: {
      return new Map<string, TemplateOption[]>([]);
    }
  }
};

export const getCategorySpecificColumns = (category: string): string[] => {
  switch (category) {
    case "litigation":
      return [
        ...COMMON_GAIA_OBJECT_FIELDS,
        "teamName",
        "initiationDate",
        "closedDate",
      ];
    case "notice":
      return [
        ...COMMON_GAIA_OBJECT_FIELDS,
        "teamName",
        "noticeDate",
        "ResponseDeadline",
        "closeDate",
      ];
    default:
      return [
        ...COMMON_GAIA_OBJECT_FIELDS,
        "teamName",
        "createdBy",
        "createdAt",
        "modifiedBy",
        "modifiedAt",
      ];
  }
};
